import { createContext, useContext, useState, useEffect, useRef, useMemo, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface LeagueTeam {
  id: string;
  name: string;
  user_id: string;
  wins: number;
  losses: number;
  ties: number;
  total_points: number;
  sabotages_used: number;
  sabotage_balance: number;
  faab_remaining: number | null;
  waiver_priority: number;
  avatar_url: string | null;
  owner_name?: string;
}

interface LeagueMember {
  id: string;
  user_id: string;
  team_id: string | null;
  role: string;
  joined_at: string;
  display_name?: string;
  team_name?: string;
}

interface League {
  id: string;
  name: string;
  invite_code: string;
  sport: string;
  status: string;
  team_count: number;
  scoring_type: string;
  scoring_format: string;
  league_type: string;
  draft_type: string;
  waiver_type: string;
  faab_budget: number;
  playoff_teams: number;
  regular_season_weeks: number;
  sabotage_enabled: boolean;
  sabotage_spin_cost: number;
  commissioner_id: string;
  category_config: any;
}

interface LeagueContextType {
  activeLeague: League | null;
  teams: LeagueTeam[];
  members: LeagueMember[];
  isLoading: boolean;
  hasLeague: boolean;
  isCommissioner: boolean;
  myTeam: LeagueTeam | null;
  setActiveLeagueId: (id: string | null) => void;
  refreshLeague: () => Promise<void>;
  renameMyTeam: (newName: string) => Promise<boolean>;
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined);

export const LeagueProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [activeLeagueId, setActiveLeagueIdRaw] = useState<string | null>(null);
  const [activeLeague, setActiveLeague] = useState<League | null>(null);
  const [teams, setTeams] = useState<LeagueTeam[]>([]);
  const [members, setMembers] = useState<LeagueMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const lastUserId = useRef<string | null>(null);

  // Wrapper: persist to localStorage when league changes
  const setActiveLeagueId = useCallback((id: string | null) => {
    setActiveLeagueIdRaw(id);
    if (id) {
      localStorage.setItem('sf_active_league_id', id);
    } else {
      localStorage.removeItem('sf_active_league_id');
    }
  }, []);

  // Clear state on sign out (user becomes null)
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      // User signed out — clear everything
      setActiveLeagueIdRaw(null);
      setActiveLeague(null);
      setTeams([]);
      setMembers([]);
      localStorage.removeItem('sf_active_league_id');
      lastUserId.current = null;
      setIsLoading(false);
      return;
    }

    // Detect user change (sign out → sign in as different user)
    const userChanged = lastUserId.current !== null && lastUserId.current !== user.id;
    lastUserId.current = user.id;

    if (userChanged) {
      // Different user signed in — clear stale league
      setActiveLeagueIdRaw(null);
      setActiveLeague(null);
      setTeams([]);
      setMembers([]);
      localStorage.removeItem('sf_active_league_id');
    }

    const findUserLeague = async () => {
      // Fetch all leagues user is a member of
      const { data: memberships } = await supabase
        .from('league_members')
        .select('league_id')
        .eq('user_id', user.id);

      const memberLeagueIds = new Set((memberships || []).map(m => m.league_id));

      if (memberLeagueIds.size === 0) {
        setIsLoading(false);
        return;
      }

      // Check if cached league is still valid for this user
      const cachedId = localStorage.getItem('sf_active_league_id');
      if (cachedId && memberLeagueIds.has(cachedId) && !userChanged) {
        setActiveLeagueIdRaw(cachedId);
        return;
      }

      // Cached league is stale or missing — deterministic default: the OLDEST
      // league the user is a member of. SAB-593: cache validation was masking an
      // unordered [0] here, so it bit exactly on a cache miss (new device, cleared
      // storage, left the cached league). Explicit ORDER BY created_at on the
      // leagues query removes the non-determinism. Last-resort keeps the old
      // membership[0] only if the leagues query itself fails.
      const { data: ordered } = await supabase
        .from('leagues')
        .select('id')
        .in('id', Array.from(memberLeagueIds))
        .order('created_at', { ascending: true })
        .limit(1);
      const firstLeagueId = ordered?.[0]?.id ?? (memberships || [])[0]?.league_id;
      if (firstLeagueId) {
        setActiveLeagueId(firstLeagueId);
      } else {
        setIsLoading(false);
      }
    };

    // Re-query on mount or when active league is null
    if (!activeLeagueId || userChanged) {
      findUserLeague();
    }
  }, [user, authLoading, activeLeagueId, setActiveLeagueId]);

  const refreshLeague = useCallback(async () => {
    if (!activeLeagueId || !user) return;
    // Don't set isLoading on refresh — only initial load should show loading state.
    // Setting isLoading here causes the entire page to flash a splash screen.

    try {
      // Fetch league, teams, and members in parallel
      const [leagueRes, teamsRes, membersRes] = await Promise.all([
        supabase
          .from('leagues')
          .select('*')
          .eq('id', activeLeagueId)
          .maybeSingle(),
        supabase
          .from('teams')
          .select('*')
          .eq('league_id', activeLeagueId)
          .order('total_points', { ascending: false }),
        supabase
          .from('league_members')
          .select('*')
          .eq('league_id', activeLeagueId),
      ]);

      // Validate user is a member of this league
      const isMember = membersRes.data?.some(m => m.user_id === user.id);
      if (!isMember) {
        console.warn('User is not a member of league', activeLeagueId, '— clearing');
        setActiveLeagueId(null);
        setActiveLeague(null);
        setTeams([]);
        setMembers([]);
        setIsLoading(false);
        return;
      }

      if (leagueRes.data) {
        setActiveLeague(leagueRes.data as League);
      }

      if (teamsRes.data) {
        // Fetch owner names for teams
        const userIds = teamsRes.data.map(t => t.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', userIds);

        const profileMap = new Map(profiles?.map(p => [p.id, p.display_name || p.username || null]) || []);
        setTeams(teamsRes.data.map(t => ({
          ...t,
          owner_name: profileMap.get(t.user_id) || t.name || 'Unknown',
        })));
      }

      if (membersRes.data) {
        // Fetch display names and team names for members
        const userIds = membersRes.data.map(m => m.user_id);
        const teamIds = membersRes.data.map(m => m.team_id).filter(Boolean) as string[];

        const [profilesRes, memberTeamsRes] = await Promise.all([
          supabase.from('profiles').select('id, display_name, username').in('id', userIds),
          teamIds.length > 0
            ? supabase.from('teams').select('id, name').in('id', teamIds)
            : Promise.resolve({ data: [] }),
        ]);

        const profileMap = new Map(profilesRes.data?.map(p => [p.id, p.display_name || p.username || null]) || []);
        const teamMap = new Map<string, string>(
          (memberTeamsRes.data || []).map(t => [t.id, t.name])
        );

        setMembers(membersRes.data.map(m => ({
          ...m,
          display_name: profileMap.get(m.user_id) || 'Unknown',
          team_name: m.team_id ? (teamMap.get(m.team_id) || 'No Team') : 'No Team',
        })));
      }
    } catch (error) {
      console.error('Failed to fetch league data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeLeagueId, user, setActiveLeagueId]);

  useEffect(() => {
    if (activeLeagueId) {
      refreshLeague();
    }
  }, [activeLeagueId, refreshLeague]);

  const isCommissioner = !!(user && activeLeague && activeLeague.commissioner_id === user.id);
  const myTeam = user ? teams.find(t => t.user_id === user.id) || null : null;

  const renameMyTeam = useCallback(async (newName: string): Promise<boolean> => {
    if (!myTeam || !activeLeagueId) return false;
    try {
      // Update team name
      const { error: teamError } = await supabase
        .from('teams')
        .update({ name: newName })
        .eq('id', myTeam.id);
      if (teamError) throw teamError;

      // Update denormalized names in matchups
      await Promise.all([
        supabase
          .from('matchups')
          .update({ home_team_name: newName })
          .eq('home_team_id', myTeam.id),
        supabase
          .from('matchups')
          .update({ away_team_name: newName })
          .eq('away_team_id', myTeam.id),
      ]);

      await refreshLeague();
      return true;
    } catch (error) {
      console.error('Failed to rename team:', error);
      return false;
    }
  }, [myTeam, activeLeagueId, refreshLeague]);

  const value = useMemo(() => ({
    activeLeague,
    teams,
    members,
    isLoading,
    hasLeague: !!activeLeague,
    isCommissioner,
    myTeam,
    setActiveLeagueId,
    refreshLeague,
    renameMyTeam,
  }), [activeLeague, teams, members, isLoading, isCommissioner, myTeam, refreshLeague, renameMyTeam]);

  return (
    <LeagueContext.Provider value={value}>
      {children}
    </LeagueContext.Provider>
  );
};

export const useLeague = () => {
  const context = useContext(LeagueContext);
  if (context === undefined) {
    throw new Error('useLeague must be used within a LeagueProvider');
  }
  return context;
};
